import { z } from 'zod';

export const LiveValueUpdateSchema = z.object({
  tag_id: z.string().min(1),
  value: z.string(),
  timestamp_ms: z.number().int().nonnegative(),
  forced: z.boolean(),
});

export type ParsedLiveValueUpdate = z.infer<typeof LiveValueUpdateSchema>;

// Sprint 3 Phase 3A (2026-05-30) — WS EventPayload::TagValue. Mirrors
// the `{kind:'tag_value', tag_id, value, ts_ms, forced}` variant of
// EventPayload in `crates/rhia-protocol/src/ws_envelope.rs`.
// `value` is always a string at the wire level — callers know the
// tag's actual type from their Tag catalog and parse accordingly.
export const EventPayloadTagValueSchema = z.object({
  kind: z.literal('tag_value'),
  tag_id: z.string().min(1),
  value: z.string(),
  ts_ms: z.number().int().nonnegative(),
  forced: z.boolean(),
});

export type ParsedEventPayloadTagValue = z.infer<typeof EventPayloadTagValueSchema>;

// ---------------------------------------------------------------------
// Unified live-sample identity (Tag Key Reconciliation, P2-B6)
// ---------------------------------------------------------------------
// `LiveValueUpdate` (Firestore `live_values` storage shape, `timestamp_ms`)
// and `EventPayloadTagValue` (WS wire frame, `ts_ms`) are two spellings of
// the SAME live sample. Their `tag_id` field carries the canonical wire
// **key** (e.g. `rs485/16/31/range_mm`); the differing timestamp field name
// is the only real divergence. Both stay as-is because they are persisted
// wire/storage contracts; this normalized shape is the single editor-facing
// view, keyed by that canonical key.

/** Editor-facing live sample, keyed by canonical wire key. */
export type LiveSample = {
  /** Canonical wire key (the `tag_id` field carries the key on the wire). */
  key: string;
  value: string;
  ts_ms: number;
  forced: boolean;
};

/**
 * Map either live-sample spelling — Firestore `LiveValueUpdate`
 * (`timestamp_ms`) or WS `EventPayloadTagValue` (`ts_ms`) — onto the shared
 * {@link LiveSample}. The wire `tag_id` becomes the normalized `key`.
 */
export function normalizeLiveSample(
  sample: ParsedLiveValueUpdate | ParsedEventPayloadTagValue,
): LiveSample {
  const ts_ms = 'ts_ms' in sample ? sample.ts_ms : sample.timestamp_ms;
  return { key: sample.tag_id, value: sample.value, ts_ms, forced: sample.forced };
}

// Sprint 3 Phase 3D/3E (2026-05-30) — runtime log frame from the
// master's defmt info/warn/error sites. `source` is a free-form
// module/task tag; `message` is the rendered line. The master
// rate-limits per (source, level) — see runtime_log.rs.
export const RuntimeLogLevelSchema = z.enum(['info', 'warn', 'error']);
export type ParsedRuntimeLogLevel = z.infer<typeof RuntimeLogLevelSchema>;

export const EventPayloadRuntimeLogSchema = z.object({
  kind: z.literal('runtime_log'),
  level: RuntimeLogLevelSchema,
  source: z.string(),
  message: z.string(),
  ts_ms: z.number().int().nonnegative(),
});
export type ParsedEventPayloadRuntimeLog = z.infer<typeof EventPayloadRuntimeLogSchema>;
